import { useState } from 'react';
import { z } from 'zod';

import Button from '../../generic/Button';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import { RouterLink } from '../../generic/Link';
import SocialLoginButtons from '../components/SocialLoginButtons';
import type { OAuthProvider } from '../../../../shared/types/routes/auth/v2';

import styles from '../Login.module.less';
import registerStyles from '../Register.module.less';

// Same validator the resolve-user signup schema uses on the backend, so the client rejects a malformed address
// before the round-trip rather than letting the server be the first to say no.
const emailSchema = z.email();

export type SignupEmailStepProps = {
  /** "Sign up", or "Sign up and join {org}" once an invite resolves. */
  heading: string;
  email: string;
  onEmailChange: (value: string) => void;
  /** The invite fixed the address, so it renders read-only. */
  isEmailLocked: boolean;
  /** Failure copy from an invite token that could not be resolved. */
  inviteAlert: string | null;
  /** That token is unusable: offer the sign-up-without-an-invite link instead of the sign-in prompt. */
  isInviteInvalid: boolean;
  /** The invite escape hatch's href (this page without the token). */
  registerWithoutInviteHref: string;
  /**
   * Submission is blocked — a resolve is already in flight, the invite is unusable or still resolving, or
   * the mount-time auth state has not landed. Gates the button *and* the form's onSubmit, since Enter
   * reaches the latter directly and would otherwise slip past the disabled button.
   */
  isBlocked: boolean;
  /** The mount-time auth-state fetch settled in error; without a way out, Continue would stay disabled forever. */
  isAuthStateError: boolean;
  onRetryAuthState: () => void;
  /** Called only with a well-formed address; the page decides what a valid submit does. */
  onSubmit: () => void;
  oauthProviders: OAuthProvider[];
  next?: string;
  inviteToken?: string;
  /** The login page, carrying the entered email and any `next`. */
  loginHref: string;
};

/**
 * The signup flow's first step: collect the email that everything downstream keys on. It owns only the
 * address's *format* check — whether that address already has accounts, and what to do about it, is the
 * page's decision, taken once `onSubmit` fires.
 */
export const SignupEmailStep = ({
  heading,
  email,
  onEmailChange,
  isEmailLocked,
  inviteAlert,
  isInviteInvalid,
  registerWithoutInviteHref,
  isBlocked,
  isAuthStateError,
  onRetryAuthState,
  onSubmit,
  oauthProviders,
  next,
  inviteToken,
  loginHref,
}: SignupEmailStepProps) => {
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleEmailChange = (value: string) => {
    onEmailChange(value);
    setEmailError(null);
  };

  const handleSubmit = () => {
    if (isBlocked) {
      return;
    }
    if (!emailSchema.safeParse(email).success) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(null);
    onSubmit();
  };

  return (
    <form
      className={styles.card}
      onSubmit={event => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <h1 className={styles.heading}>{heading}</h1>
      {inviteAlert && (
        <p role="alert" className={styles.error}>
          {inviteAlert}
        </p>
      )}
      <div className={styles.fieldGroup}>
        <LabeledTextInput
          label="Email"
          type="email"
          name="email"
          placeholder="Enter your email"
          value={email}
          autoFocus
          isRequired
          isReadOnly={isEmailLocked}
          onChange={handleEmailChange}
        />
      </div>
      {emailError && (
        <p role="alert" className={styles.error}>
          {emailError}
        </p>
      )}
      {/* A settled auth-state failure would otherwise present as a forever-disabled Continue with no way out. */}
      {isAuthStateError && (
        <>
          <p role="alert" className={styles.error}>
            Could not load sign-up options. Please try again.
          </p>
          <Button type="anchor" size="small" onClick={onRetryAuthState}>
            Retry
          </Button>
        </>
      )}
      <Button isFullWidth size="large" isDisabled={isBlocked} onClick={handleSubmit}>
        Continue
      </Button>
      {!isInviteInvalid && (
        <SocialLoginButtons providers={oauthProviders} next={next} variant="signup" inviteToken={inviteToken} />
      )}
      <div className={registerStyles.altAction}>
        {isInviteInvalid ? (
          <RouterLink to={registerWithoutInviteHref}>Sign up without an invite</RouterLink>
        ) : (
          <>Already have an account? <RouterLink to={loginHref}>Sign in</RouterLink></>
        )}
      </div>
    </form>
  );
};
